import { ApiError } from "@/lib/api-client";
import { formatWaitMinutes, readRetryAfterSeconds } from "@/lib/retry-after";
import { toAuthBanner, type AuthBanner } from "@/routes/sign-in/auth-error";

// Maps a thrown change-password error to a UI outcome. Branches ONLY on the
// structured `error.code` / `error.status`; copy never echoes a password, token, or
// raw backend string.
//
// [Source: src/application/authentication/change_password — AUTH-001 wrong current,
//  AUTH-010 PasswordUnchanged, 403 UserDeactivated, 429 AUTH-016 rate limited;
//  Dev Notes Task 3 error table.]
export type ForcedChangeFieldName = "currentPassword" | "newPassword";

export interface ForcedChangeError {
  /** Field to flag inline, or null. */
  fieldName: ForcedChangeFieldName | null;
  /** Inline message for that field, or null. */
  fieldMessage: string | null;
  /** Banner descriptor, or null. */
  banner: AuthBanner | null;
}

/**
 * 429 AUTH-016 — the per-user change-password limiter (Story 14.9). A SETTLED
 * state, so `info` and not `danger`: the window rolls forward on its own, and
 * alarming copy would report a wait as a fault. The same posture the sibling
 * change-EMAIL limiter (AUTH-015) already takes.
 *
 * Naming the wait is the whole point. Without it the user's only strategy is to
 * keep retrying — which is exactly what the limiter exists to stop — and the
 * worst-placed instance of that is the FORCED-change screen, where a legacy
 * clinician mistyping the temporary password from their invitation email is
 * locked out of onboarding by a message that explains nothing.
 *
 * Static minutes, never a live countdown: the sibling banner has none, and two
 * treatments of one concept across two adjacent dialogs is worse than neither
 * having one. No duration on the wire → say so without one, rather than
 * inventing a number.
 */
function rateLimitedBanner(details: unknown): AuthBanner {
  const seconds = readRetryAfterSeconds(details);
  return {
    variant: "info",
    message:
      seconds === null
        ? "Too many password-change attempts. Try again later."
        : `Too many password-change attempts. Try again in about ${formatWaitMinutes(seconds)}.`,
  };
}

export function toForcedChangeError(error: unknown): ForcedChangeError {
  if (error instanceof ApiError) {
    // Wrong temp password — inline on the current-password field plus a danger banner.
    if (error.code === "AUTH-001") {
      return {
        fieldName: "currentPassword",
        fieldMessage: "The current password is incorrect.",
        banner: { variant: "danger", message: "The current password is incorrect." },
      };
    }
    // New password equals current — inline on the new-password field (no banner needed).
    if (error.code === "AUTH-010") {
      return {
        fieldName: "newPassword",
        fieldMessage: "New password must differ from your current password.",
        banner: null,
      };
    }
    // Server-side validation the client Zod schema did not pre-catch (e.g. the new
    // password exceeds the backend max length). Surface inline on the new-password
    // field — never echo `error.details`, which can repeat the input.
    if (error.status === 422) {
      return {
        fieldName: "newPassword",
        fieldMessage: "Choose a password that meets the requirements.",
        banner: null,
      };
    }
    // Rate limited. The CODE first — it is the contract Story 14.9 wrote — with
    // `status === 429` as the backstop for a limiter that ever answers with a
    // different one: on this endpoint every 429 is this lockout, and falling
    // through to "Something went wrong. Please try again." would tell the user
    // to do the one thing that cannot work.
    if (error.code === "AUTH-016" || error.status === 429) {
      return {
        fieldName: null,
        fieldMessage: null,
        banner: rateLimitedBanner(error.details),
      };
    }
    // Account deactivated between sign-in and this submit.
    if (error.status === 403) {
      return {
        fieldName: null,
        fieldMessage: null,
        banner: {
          variant: "danger",
          message: "This account is not active. Contact your administrator.",
        },
      };
    }
  }
  // AUTH-503 / NETWORK_ERROR / unknown → shared mapper (warn/generic, no internals).
  return { fieldName: null, fieldMessage: null, banner: toAuthBanner(error) };
}
